// Shared IndexedDB plumbing for the per-feature offline snapshots (Infrared
// Offline, LOTO Offline). Each feature gets its own set of localforage stores
// (`${prefix}_offline_locations|snapshot|refs|photos`), all in the "bowvue" DB;
// what goes IN the snapshot, the tree shape, the download queries, the
// snapshot walkers, stays feature-specific in the module that owns it.
//
// The queued work (mutation outbox / upload queue) is NOT here: those stores
// are shared across features and scoped by the `feature` tag instead (see
// outbox.matchesFeature).
import localforage from 'localforage'
import { notifyOfflineQueues } from './offline-events'

// How many existing photos to fetch concurrently. Small: field laptops on
// hotel wifi are the target hardware.
const PHOTO_FETCH_CONCURRENCY = 5

export function createOfflineStore(prefix) {
  const locationsStore = localforage.createInstance({
    name: 'bowvue',
    storeName: `${prefix}_offline_locations`,
    description: `Locations downloaded for offline ${prefix} work`,
  })
  const snapshotStore = localforage.createInstance({
    name: 'bowvue',
    storeName: `${prefix}_offline_snapshot`,
    description: `Offline ${prefix} snapshot per location`,
  })
  const refsStore = localforage.createInstance({
    name: 'bowvue',
    storeName: `${prefix}_offline_refs`,
    description: `Reference data per location (${prefix})`,
  })
  const photoStore = localforage.createInstance({
    name: 'bowvue',
    storeName: `${prefix}_offline_photos`,
    description: `Existing photo blobs downloaded for offline viewing (${prefix})`,
  })

  return {
    async listLocations() {
      const items = []
      await locationsStore.iterate((value) => {
        items.push(value)
      })
      return items.sort((a, b) => b.downloadedAt - a.downloadedAt)
    },

    async getLocation(locationId) {
      return locationsStore.getItem(String(locationId))
    },

    // A download finished: the app bar's downloads chip recounts.
    async saveLocationRecord(record) {
      await locationsStore.setItem(String(record.locationId), record)
      notifyOfflineQueues()
      return record
    },

    // Patch fields on a location record (pendingCount, status, …).
    async updateLocation(locationId, patch) {
      const record = await locationsStore.getItem(String(locationId))
      if (!record) return null
      const next = { ...record, ...patch }
      await locationsStore.setItem(String(locationId), next)
      return next
    },

    async getSnapshot(locationId) {
      return snapshotStore.getItem(String(locationId))
    },

    // Persist an edited snapshot. The JSON round-trip is load-bearing: the
    // editor hands us Vue reactive proxies, and IndexedDB's structured clone
    // refuses a Proxy outright ("DataCloneError"). The tree is plain GraphQL
    // JSON, so the round-trip loses nothing.
    async saveSnapshot(locationId, tree) {
      await snapshotStore.setItem(
        String(locationId),
        JSON.parse(JSON.stringify(tree))
      )
    },

    async getRefs(locationId) {
      return refsStore.getItem(String(locationId))
    },

    async saveRefs(locationId, refs) {
      await refsStore.setItem(String(locationId), refs)
    },

    async getPhotoBlob(key) {
      return photoStore.getItem(key)
    },

    async savePhotoBlob(key, blob) {
      await photoStore.setItem(key, blob)
    },

    // Fetch existing photo binaries into the blob store so they render
    // offline. A blob already stored is final, photo objects are immutable,
    // so an interrupted or repeated download resumes instead of re-fetching.
    // Individual failures are tolerated but counted.
    async downloadPhotos(refs, onProgress) {
      let done = 0
      let failed = 0
      for (let i = 0; i < refs.length; i += PHOTO_FETCH_CONCURRENCY) {
        const batch = refs.slice(i, i + PHOTO_FETCH_CONCURRENCY)
        await Promise.all(
          batch.map(async (ref) => {
            try {
              const cached = await photoStore.getItem(ref.key)
              if (!cached) {
                const res = await fetch(ref.url)
                if (!res.ok) {
                  throw new Error('photo fetch failed: ' + res.status)
                }
                const blob = await res.blob()
                await photoStore.setItem(ref.key, blob)
              }
            } catch (err) {
              failed += 1
            } finally {
              done += 1
              if (onProgress) onProgress({ done, total: refs.length })
            }
          })
        )
      }
      return { failed }
    },

    // Drop everything stored for a location (after a successful sync, or a
    // manual "remove download"). Queued outbox/upload items are the caller's
    // to drain or discard. This only owns the snapshot stores. Photo keys are
    // `${locationId}:…` by convention, hence the prefix sweep.
    async removeLocation(locationId) {
      await snapshotStore.removeItem(String(locationId))
      await refsStore.removeItem(String(locationId))
      await locationsStore.removeItem(String(locationId))
      notifyOfflineQueues()
      const keyPrefix = `${locationId}:`
      const keys = await photoStore.keys()
      for (const key of keys) {
        if (key.startsWith(keyPrefix)) await photoStore.removeItem(key)
      }
    },
  }
}

// Ask the browser to exempt this origin from storage eviction: the whole point
// of a download is data sitting in IndexedDB for days on a field laptop, and
// browsers (Safari especially) may otherwise clear it under storage pressure.
// Best-effort. A denial changes nothing functionally.
export function requestStoragePersistence() {
  if (
    typeof navigator !== 'undefined' &&
    navigator.storage &&
    typeof navigator.storage.persist === 'function'
  ) {
    navigator.storage.persist().catch(() => {})
  }
}
